// Global
import {
  useSitecoreContext,
  Text,
  GetStaticComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { Options, Splide } from '@splidejs/react-splide';
import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';

// Lib
import { ComponentProps } from 'lib/component-props';
import {
  ProductAndArticleComponent,
  CardComponents,
  Ingredients,
} from 'lib/templates/Feature.Dart.model';
import { DartPages } from 'lib/templates/Project.Dart.model';
import { Common } from 'lib/templates/Foundation.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';
import graphQLClientFactory from 'lib/graphql-client-factory';

// Local
import ProductCarousel from 'components/authorable/Carousel/ProductCarousel';
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import Label, { ColorTypes } from 'helpers/Label/Label';
import ProductEan from 'helpers/MikMak/ProductEan';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ShareButton from 'helpers/ShareButton/ShareButton';
import { useRealPathName } from 'lib/utils/use-real-pathname';
import Adimo from 'helpers/Adimo/Adimo';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import { tailwindconstiants } from './ProductInformationTailwind';
import { useTheme } from 'lib/context/ThemeContext';
import { ComponentVariants } from 'lib/context/ComponentVariants';
import Button from 'helpers/Button/Button';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import { useI18n } from 'next-localization';

export type ProductInformationProps = ComponentProps & { staticProps?: Result };

export type ProductInformation = ItemEx &
  ProductAndArticleComponent.ProductInformation.ProductInformation;

export type FormulaInfo = Ingredients.Formula;

export type BadgeInformation = ItemEx & CardComponents.ProductCard.Badge;

export type CategoryInfo = ItemEx & DartPages.CategoryListPage;
export type LabelInfo = ItemEx & Common.Label.Label;

export type SecondaryImages = ProductAndArticleComponent.ProductInformation.SecondaryImage &
  ProductAndArticleComponent.ProductInformation.SecondaryVideo;

export type Ingredient = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
  };
};

export type Role = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
    definition: {
      value: string;
    };
  };
};
export type Language = {
  name: string;
};
export type ActiveCountry = {
  name: string;
  item: {
    languageList: {
      targetItems: Language[];
    };
  };
  id: string;
};
export type FormulaIngredient = {
  ingredient: Ingredient;
  role: Role;
  allergen: {
    value: string;
  };
  activeIngredientCountryMapping: {
    targetItems: ActiveCountry[];
  };
};

export type FormulaIngredients = {
  item: {
    name: string;
    children: {
      results: FormulaIngredient[];
    };
  };
};

export type Result = {
  isRegistered: boolean;
  wiscj_pdf_url: string | null;
  wiscj_brand_pdp_page: string | null;
  wiscj_brand_name: string | null;
};

const ProductInformation = (props: ProductInformationProps): JSX.Element => {
  const path = useRealPathName();
  const { sitecoreContext } = useSitecoreContext();

  const enableBazaarSetting = sitecoreContext?.bazaarVoice?.enableBazaarVoice as
    | boolean
    | undefined;

  const enablePowerReviewSetting = sitecoreContext?.powerReviews?.enablePowerReviews as
    | boolean
    | undefined;

  const { badge, category, productInformation, subCategory, parallelCategory } =
    sitecoreContext?.route?.fields || {};

  const badgeInfo = badge as BadgeInformation;
  const categoryInfo = category as CategoryInfo[];
  const categories = Array.isArray(categoryInfo)
    ? categoryInfo
        ?.filter((obj) => obj.fields?.categoryType?.value == 'Category')
        ?.map((c) => c.fields?.categoryNameForSearchIndex?.value)
        ?.join('|')
    : '';
  const secondaryCategories = Array.isArray(categoryInfo)
    ? categoryInfo
        ?.filter((obj) => obj.fields?.categoryType?.value == 'Secondary Category')
        ?.map((c) => c.fields?.categoryNameForSearchIndex?.value)
        ?.join('|')
    : '';
  const tertiaryCategories = Array.isArray(categoryInfo)
    ? categoryInfo
        ?.filter((obj) => obj.fields?.categoryType?.value == 'Tertiary Category')
        ?.map((c) => c.fields?.categoryNameForSearchIndex?.value)
        ?.join('|')
    : '';
  const parallelCategories = Array.isArray(parallelCategory)
    ? parallelCategory
        ?.map((c: CategoryInfo) => c.fields?.categoryNameForSearchIndex?.value)
        ?.join('|')
    : '';
  const prodInfo = productInformation as ProductInformation;
  const subCategoryInfo = subCategory as CategoryInfo;
  const isMikMak = sitecoreContext?.integrationMikMak?.isMikMak === '1';
  const adimoMultiTouchPoint = sitecoreContext?.integrationEvidon?.isAdimoMultiTouchpoint === '1';
  const isAdimoPriceFirst = sitecoreContext?.integrationEvidon?.isAdimoPriceFirst === '1';
  const [initialLoad, setInitialLoad] = useState(0);
  const { themeName } = useTheme();
  const componentVariants = ComponentVariants();
  const i18n = useI18n();
  const FormulaText = i18n.t('Formula');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contentDetails = sitecoreContext?.contentDetails as any;

  useEffect(() => {
    const randomValue = Math.floor(Math.random() * 100) + 1;

    setInitialLoad(randomValue);
  }, [path]);

  const {
    headline,
    description,
    label,
    additionalDetails,
    socialShare,
    ratingAndReview,
    disclaimerText,
    label2,
    //links,
  } = prodInfo?.fields || {};

  const pipeSeparatedLabelTitle = Array.isArray(label)
    ? label?.map((item: LabelInfo) => item?.fields?.link?.value?.text).join('|')
    : '';

  const pipeSeparatedLabelhref = Array.isArray(label)
    ? label?.map((item: LabelInfo) => item?.fields?.link?.value?.href).join('|')
    : '';

  const pipeSeparatedLabel2Title = Array.isArray(label2)
    ? label2?.map((item: LabelInfo) => item?.fields?.link?.value?.text).join('|')
    : '';

  const pipeSeparatedLabel2href = Array.isArray(label2)
    ? label2?.map((item: LabelInfo) => item?.fields?.link?.value?.href).join('|')
    : '';

  const isPrimaryImageAvailable = prodInfo?.fields?.primaryImage?.value?.src;

  const {
    base,
    productInformationLeftWrapper,
    productInformationRightWrapper,
    productInformationTitleWrapper,
    productInformationTopRightWrapper,
    productInformationMiddleRightWrapper,
    productInformationDescriptionWrapper,
    productInformationDescription,
    formulaText,
    productInformationAdditionalDetails,
    productInformationRatingReviewWrap,
    productInformationRatingWrap,
    productInformationStarRating,
    productInformationLinksWrapper,
    productInformationLinks,
    productInformationBG,
    socialShareAlignment,
    buyNowBtnWrapper,
    legalDisclaimerText,
    labelColor,
  } = tailwindconstiants({
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    isPrimaryImageAvailable: !!isPrimaryImageAvailable,
  });

  const enableRTL = sitecoreContext?.enableRTL as boolean | undefined;
  const direction = enableRTL ? 'rtl' : 'ltr';

  const mainSliderRef = useRef<Splide>(null);
  const thumbnailSliderRef = useRef<Splide>(null);
  const mainSlider: Options = {
    type: 'fade',
    rewind: false,
    pagination: false,
    arrows: false,
    autoplay: false,
    resetProgress: false,
  };

  const thumbSlider: Options = {
    arrows: false,
    fixedWidth: 120,
    fixedHeight: 120,
    gap: 16,
    rewind: false,
    isNavigation: true,
    perPage: 3,
    pagination: false,
    direction: direction,
    breakpoints: {
      600: {
        fixedWidth: 84,
        fixedHeight: 84,
        gap: 10,
      },
    },
  };

  const id = props?.params?.RenderingIdentifier;
  const isRegistered = props?.staticProps?.isRegistered;
  const wiscj_pdf_url = props?.staticProps?.wiscj_pdf_url;
  const wiscj_brand_pdp_page = props?.staticProps?.wiscj_brand_pdp_page;
  const wiscj_brand_name = props?.staticProps?.wiscj_brand_name;

  let wiscj_link_title = contentDetails?.wiscjBrandLinkText?.replace('{brand}', 'brand');
  if (wiscj_brand_name) {
    const replacementNameValues = contentDetails?.wiscjBrandMappings;
    let replaced = false;
    replacementNameValues
      ?.split('&')
      .forEach((entry: { split: (arg0: string) => [string, string] }) => {
        const [name, rawValue] = entry.split('=');
        if (!name || !rawValue) return;
        const value = decodeURIComponent(rawValue);
        if (wiscj_brand_name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase()) {
          wiscj_link_title = contentDetails?.wiscjBrandLinkText?.replace('{brand}', value);
          replaced = true;
        }
      });
    if (!replaced) {
      wiscj_link_title = contentDetails?.wiscjBrandLinkText?.replace('{brand}', wiscj_brand_name);
    }
  }
  const formula = (prodInfo?.fields?.formula as FormulaInfo) ?? {};

  const upcs = [
    ...new Set(prodInfo?.fields?.variantItems?.map((vi) => vi?.fields?.ean?.value)),
  ].join(',');
  // const adimoMultiTouchpointValues = prodInfo?.fields?.wheretoBuy?.value;
  const combinedValues = [
    prodInfo?.fields?.addtoCart?.value,
    prodInfo?.fields?.buyOnline?.value,
    prodInfo?.fields?.wheretoBuy?.value,
  ].join(', ');

  const buyOnline = prodInfo?.fields?.buyOnline?.value;
  const brand = sitecoreContext?.site?.name;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const jsonLdProduct = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': currentUrl,
    name: prodInfo?.fields?.headline?.value || '',
    image: prodInfo?.fields?.primaryImage?.value?.src || '',
    description: prodInfo?.fields?.description?.value || '',
    sku: '',
    brand: brand,
  };

  return (
    <div
      className={productInformationBG()}
      data-component="authorable/productinformation"
      id={id ? id : undefined}
      tabIndex={id ? -1 : 1}
    >
      {enableBazaarSetting && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdProduct) }}
          />
        </Head>
      )}

      <Container>
        <div className={base()}>
          {isPrimaryImageAvailable && (
            <div className={productInformationLeftWrapper()}>
              <ProductCarousel
                mainSliderRef={mainSliderRef}
                thumbnailSliderRef={thumbnailSliderRef}
                props={prodInfo}
                mainSlider={mainSlider}
                thumbSlider={thumbSlider}
                key={path + initialLoad}
                carouselNavigation={componentVariants?.productInformation?.carousel?.navigation}
                carouselPagination={componentVariants?.productInformation?.carousel?.pagination}
              ></ProductCarousel>
            </div>
          )}
          <div className={productInformationRightWrapper()}>
            <div className={productInformationTopRightWrapper()}>
              <>
                {themeName !== 'WISCJ' ? (
                  <>
                    {Array.isArray(label) &&
                      label.map((item: LabelInfo, index: number) => (
                        <Label
                          backgroundColor="bg-components-product-information-color-bg"
                          color={
                            (componentVariants?.productInformation?.label as ColorTypes) || 'white'
                          }
                          className={labelColor()}
                          link={item?.fields?.link}
                          key={index}
                        />
                      ))}
                    <span className="product-label hidden">
                      <span data-href={pipeSeparatedLabelhref}>{pipeSeparatedLabelTitle}</span>
                    </span>
                  </>
                ) : (
                  <>
                    {Array.isArray(label2) &&
                      label2.map((item: LabelInfo, index: number) => (
                        <Label
                          backgroundColor="bg-components-product-information-color-bg"
                          color={
                            (componentVariants?.productInformation?.label as ColorTypes) || 'white'
                          }
                          className={labelColor()}
                          link={item?.fields?.link}
                          key={index}
                        />
                      ))}
                    <span className="product-label hidden">
                      <span data-href={pipeSeparatedLabel2href}>{pipeSeparatedLabel2Title}</span>
                    </span>
                  </>
                )}
              </>
              {socialShare?.value && (
                <div className={socialShareAlignment()}>
                  {' '}
                  <ShareButton />
                </div>
              )}
            </div>
            <div className={productInformationMiddleRightWrapper()}>
              {headline?.value && (
                <div className={productInformationTitleWrapper()}>
                  <Text encode={false} field={headline} tag="h1" />
                </div>
              )}
              {ratingAndReview?.value &&
                (prodInfo?.id && enableBazaarSetting ? (
                  <div
                    data-bv-show="rating_summary"
                    data-bv-product-id={(prodInfo?.fields?.oldPageId?.value || prodInfo?.id)
                      ?.replace(/-/g, '')
                      .toUpperCase()}
                  ></div>
                ) : (
                  enablePowerReviewSetting && (
                    <div className={productInformationRatingReviewWrap()}>
                      <div className={productInformationRatingWrap()}>
                        <div className={productInformationStarRating()}>
                          <div id="pr-reviewsnippet"></div>
                        </div>
                      </div>
                    </div>
                  )
                ))}
              {additionalDetails?.value && (
                <div className={productInformationAdditionalDetails()}>
                  <RichTextA11yWrapper field={additionalDetails}></RichTextA11yWrapper>
                </div>
              )}
              {isMikMak && (
                <div className={buyNowBtnWrapper()}>
                  <ProductEan
                    eans={upcs}
                    btnType={componentVariants?.productInformation?.primaryCta?.type}
                    btnColor={componentVariants?.productInformation?.primaryCta?.color}
                  />
                </div>
              )}
              {!isMikMak && (
                <>
                  {adimoMultiTouchPoint && (
                    <div className={buyNowBtnWrapper()}>
                      <Adimo
                        touchpointIDs={combinedValues}
                        isMultiTouchPoint={true}
                        type={componentVariants?.productInformation?.primaryCta?.type}
                        color={componentVariants?.productInformation?.primaryCta?.color}
                      />
                    </div>
                  )}
                  {isAdimoPriceFirst && (
                    <div className={buyNowBtnWrapper()}>
                      {' '}
                      <Adimo
                        touchpointIDs={buyOnline}
                        isMultiTouchPoint={false}
                        type={componentVariants?.productInformation?.primaryCta?.type}
                        color={componentVariants?.productInformation?.primaryCta?.color}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {isRegistered !== undefined && !isRegistered && formula?.fields?.title?.value && (
              <div className={formulaText()}>
                {FormulaText}{' '}
                <Text encode={false} field={{ value: formula?.fields?.title?.value }} />
              </div>
            )}
            <div className={productInformationDescriptionWrapper()}>
              {description && themeName !== 'WISCJ' ? (
                <div className={productInformationDescription()}>
                  <RichTextA11yWrapper field={description} />
                </div>
              ) : (
                <>
                  {/* Links */}

                  {wiscj_pdf_url && (
                    <div className={productInformationLinksWrapper()}>
                      <div className="w-6 h-6 mr-2">
                        <GoogleMaterialSymbol icon="wiscj_link" />
                      </div>
                      <div className={productInformationLinks()}>
                        <Button
                          label={contentDetails?.wiscjCaIngredientsDisclosureLinkText}
                          title={contentDetails?.wiscjCaIngredientsDisclosureLinkText}
                          type="text"
                          tag="a"
                          href={wiscj_pdf_url}
                          target={'_blank'}
                        />
                      </div>
                    </div>
                  )}

                  {!isRegistered && wiscj_brand_pdp_page && (
                    <div className={productInformationLinksWrapper()}>
                      <div className="w-6 h-6 mr-2">
                        <GoogleMaterialSymbol icon="wiscj_link" />
                      </div>
                      <div className={productInformationLinks()}>
                        {' '}
                        <Button
                          label={wiscj_link_title}
                          title={wiscj_link_title}
                          type="text"
                          tag="a"
                          href={wiscj_brand_pdp_page}
                          target={'_blank'}
                        />
                      </div>
                    </div>
                  )}

                  {!isRegistered && contentDetails?.wiscjLinkToSDSUrl && (
                    <div className={productInformationLinksWrapper()}>
                      <div className="w-6 h-6 mr-2">
                        <GoogleMaterialSymbol icon="wiscj_link" />
                      </div>
                      <div className={productInformationLinks()}>
                        {' '}
                        <Button
                          label={contentDetails?.wiscjLinkToSDSText}
                          title={contentDetails?.wiscjLinkToSDSText}
                          type="text"
                          tag="a"
                          href={contentDetails?.wiscjLinkToSDSUrl}
                          target={'_blank'}
                        />
                      </div>
                    </div>
                  )}

                  {/* {links?.map((link, index) => (
                    <>
                      <div className={productInformationLinksWrapper()}>
                        <div className="w-6 h-6 mr-2">
                          <GoogleMaterialSymbol icon="wiscj_link" />
                        </div>
                        <div className={productInformationLinks()}>
                          <Button
                            label={link?.displayName}
                            title={link?.displayName}
                            type="text"
                            tag="a"
                            href={(link?.fields?.link as { value: { href: string } })?.value?.href}
                            key={index}
                            target={
                              (link?.fields?.link as { value: { target: string } })?.value?.target
                            }
                          />
                        </div>
                      </div>
                    </>
                  ))} */}
                </>
              )}
            </div>
            {disclaimerText?.value != '' && (
              <LegalDisclaimer
                disclaimerText={disclaimerText}
                disclaimerClasses={legalDisclaimerText()}
              />
            )}
          </div>
        </div>
        <input
          type="hidden"
          name="hdnProductBadge"
          id="hdnProductBadge"
          value={badgeInfo?.fields?.badgeTitle?.value}
        />
        <input type="hidden" name="hdnProductCategory" id="hdnProductCategory" value={categories} />
        <input
          type="hidden"
          name="hdnProductSecondaryCategory"
          id="hdnProductSecondaryCategory"
          value={secondaryCategories}
        />
        <input
          type="hidden"
          name="hdnProductTertiaryCategory"
          id="hdnProductTertiaryCategory"
          value={tertiaryCategories}
        />
        <input
          type="hidden"
          name="hdnParallelCategory"
          id="hdnParallelCategory"
          value={parallelCategories}
        />
        <input
          type="hidden"
          name="hdnProductSubCategory"
          id="hdnProductSubCategory"
          value={subCategoryInfo?.fields?.categoryNameForSearchIndex?.value}
        />
        <input
          type="hidden"
          name="hdnProductInfoItemId"
          id="hdnProductInfoItemId"
          value={
            prodInfo?.fields?.oldPageId?.value || prodInfo?.id?.replace(/-/g, '').toUpperCase()
          }
        />
        <input
          type="hidden"
          name="hdnProductPageItemId"
          id="hdnProductPageItemId"
          value={sitecoreContext?.route?.itemId?.replace(/-/g, '').toUpperCase()}
        />
        <input
          type="hidden"
          name="hdnAdimoAddtoCart"
          id="hdnAdimoAddtoCart"
          value={prodInfo?.fields?.addtoCart?.value}
        />
        <input
          type="hidden"
          name="hdnAdimoBuyOnline"
          id="hdnAdimoBuyOnline"
          value={prodInfo?.fields?.buyOnline?.value}
        />
        <input
          type="hidden"
          name="hdnAdimoWheretoBuy"
          id="hdnAdimoWheretoBuy"
          value={prodInfo?.fields?.wheretoBuy?.value}
        />
      </Container>
      {/* <>
        <div id="pr-reviewdisplay">
          <h2 className="prReviewHeading" id="prReviewHeading">
            Ratings &amp; Review
          </h2>
        </div>
      </> */}
    </div>
  );
};

export default ProductInformation;

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const graphQLClient = graphQLClientFactory({});
  const site = layoutData?.sitecore?.context?.site?.name;
  if (site?.toLocaleLowerCase() !== 'wiscj') {
    return {
      staticProps: null,
    };
  }
  const formula =
    layoutData?.sitecore?.route?.fields?.productInformation &&
    'fields' in layoutData?.sitecore?.route?.fields?.productInformation
      ? layoutData?.sitecore?.route?.fields?.productInformation?.fields?.formula
      : null;
  const formulaId = formula && 'id' in formula ? formula?.id : '';

  const language = layoutData?.sitecore?.context?.language;

  const data = await graphQLClient.request<FormulaIngredients>(query, {
    path: formulaId,
    language: language,
  });

  const wiscj_pdf_path =
    '/sitecore/media library/Images/CA-ID-PDF/' + data?.item?.name?.replace('_', '-');
  const wiscj_pdf_result = await graphQLClient.request<MediaItemQueryResult>(mediaItemQuery, {
    path: wiscj_pdf_path,
    language: 'en', // layoutData?.sitecore?.context?.language, // do these need to be in other locals?
  });

  const pi_id =
    layoutData?.sitecore?.route?.fields?.productInformation &&
    'fields' in layoutData?.sitecore?.route?.fields?.productInformation
      ? layoutData?.sitecore?.route?.fields?.productInformation?.id
      : '';

  let brand_name: string | null = null;
  const path = layoutData?.sitecore?.context?.itemPath;
  const path_arr = path?.split('/');
  if (path_arr?.length && path_arr?.length > 2) {
    brand_name = path_arr[2].trim().toLowerCase() || null;
  }
  let brand_pdp_page = null;
  if (pi_id !== '') {
    const wiscj_pdp_url_result = await graphQLClient.request<FindPDPUrlResult>(findPDPUrl, {
      pdp_id: '{' + pi_id + '}',
      language: layoutData?.sitecore?.context?.language,
    });
    if (wiscj_pdp_url_result?.search?.results.length > 0) {
      for (const result of wiscj_pdp_url_result?.search?.results) {
        if (brand_name && result.path.toLowerCase().includes('/' + brand_name + '/')) {
          brand_pdp_page = result.url.url;
          brand_pdp_page = brand_pdp_page.split('/').slice(0, 4).join('/'); // trim off everything after locale
          break;
        }
      }
      // no match, take first one in list
      if (!brand_pdp_page) {
        brand_pdp_page = wiscj_pdp_url_result?.search?.results[0]?.url?.url;
        brand_pdp_page = brand_pdp_page.split('/').slice(0, 4).join('/'); // trim off everything after locale
        // use brand name for first result
        brand_name = wiscj_pdp_url_result?.search?.results[0]?.path.split('/')[4]; // returns an array ['', 'sitecore', 'content', 'Dart', BRAND_NAME, ...]
      }
    }
  }

  let wiscj_pdf_url = null;
  if (wiscj_pdf_result?.item?.url?.url) {
    wiscj_pdf_url = wiscj_pdf_result?.item?.url?.url;
  }
  const formulaIngredients = data && data?.item?.children?.results;

  const result: Result = {
    isRegistered: false,
    wiscj_pdf_url: wiscj_pdf_url,
    wiscj_brand_pdp_page: brand_pdp_page,
    wiscj_brand_name: brand_name,
  };

  if (formulaIngredients && formulaIngredients.length > 0) {
    for (const formulaIngredient of formulaIngredients) {
      const activeCountries = formulaIngredient?.activeIngredientCountryMapping?.targetItems;
      if (activeCountries && activeCountries.length > 0) {
        for (const country of activeCountries) {
          const countryData = await graphQLClient.request<ActiveCountry>(countryQuery, {
            path: country?.id,
            language: 'en',
          });
          const languageItems = countryData?.item?.languageList?.targetItems;
          for (const lang of languageItems) {
            if (language?.toLocaleLowerCase() === lang?.name?.toString().toLocaleLowerCase()) {
              result.isRegistered = true;
              break;
            }
          }
        }
      }
    }
  }
  return {
    staticProps: result,
  };
};

export const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children {
        total
        results {
          ... on FormulaIngredient {
            name
            allergen: field(name: "allergen") {
              value
            }
            ingredient: field(name: "ingredient") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                }
              }
            }
            role: field(name: "role") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            activeIngredientCountryMapping: field(name: "activeIngredientCountryMapping") {
              ... on MultilistField {
                targetItems {
                  ... on CountryName {
                    name
                    id
                    title: field(name: "title") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const countryQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      languageList: field(name: "languageList") {
        ... on MultilistField {
          targetItems {
            id
            name
          }
        }
      }
    }
  }
`;

type MediaItemQueryResult = {
  item: {
    id: string;
    name: string;
    url: {
      url: string;
    };
  };
};

const mediaItemQuery = /* GraphQL */ `
  query MediaItemQueryy($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      id
      name
      url {
        url
      }
    }
  }
`;

type FindPDPUrlResult = {
  search: {
    results: {
      path: string;
      url: {
        url: string;
      };
    }[];
  };
};

const findPDPUrl = /* GraphQL */ `
  query pdp_url_query($pdp_id: String!, $language: String!) {
    search(
      where: {
        AND: [
          { name: "_language", value: $language, operator: EQ }
          { name: "_templates", value: "{E3396219-CDFA-4F7B-B6E8-08EC87ED410B}", operator: EQ }
          { name: "_path", value: "{3788F8CB-123D-4CA1-976A-4AC9F2D716E6}", operator: NCONTAINS }
          { name: "productInformation", value: $pdp_id, operator: EQ }
        ]
      }
      first: 10
    ) {
      results {
        path
        url {
          url
        }
      }
    }
  }
`;
